import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, ChevronDown, Users, Calendar, MapPin, Megaphone, Sparkles, Receipt } from 'lucide-react';
import type { ReferralSignupOffer } from '../../lib/referralPartnerOffer';
import { formatReferralDiscountLabel } from '../../lib/referralPartnerOffer';
import { setCurrentSignupFlow, trackSignupEvent, trackSignupEventOnce } from '../../lib/signupAnalytics';

interface PartnerLandingProps {
  /** Valid, active partner offer — this page only renders for partner links. */
  offer: ReferralSignupOffer;
}

/** Laurel-style stat badge (top of the hero). */
function StatBadge({ top, bottom }: { top: React.ReactNode; bottom: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-white/25 text-xl leading-none select-none">{'\u2e28'}</span>
      <div className="text-center">
        <div className="text-white font-bold text-[15px] leading-tight flex items-center justify-center gap-1">{top}</div>
        <div className="text-white/50 text-[11px] leading-tight">{bottom}</div>
      </div>
      <span className="text-white/25 text-xl leading-none select-none">{'\u2e29'}</span>
    </div>
  );
}

const FEATURES = [
  { icon: Users, title: 'CRM & Leads', text: 'Every customer, conversation, and opportunity in one place.' },
  { icon: Calendar, title: 'Scheduling', text: 'Team calendars, online booking, and automated reminders.' },
  { icon: MapPin, title: 'Field Management', text: 'Mobile jobs, routes, service areas, and live tracking.' },
  { icon: Receipt, title: 'Invoicing & Payments', text: 'Quotes, invoices, and payments that follow the job.' },
  { icon: Megaphone, title: 'Marketing', text: 'Email & SMS campaigns that fill your calendar.' },
  { icon: Sparkles, title: 'AI Everywhere', text: 'AI that replies, books, and works your leads for you.' },
];

/**
 * Partner-link landing page (dark hero). Shows who invited the visitor and
 * flows into the extended signup at /sign-up-new-business. The referral code
 * is already persisted by the /signup entry, so attribution and the partner's
 * first-payment discount carry through automatically.
 */
export default function PartnerLanding({ offer }: PartnerLandingProps) {
  const navigate = useNavigate();
  const hasDiscount = !!(offer.signup_discount_enabled && offer.signup_discount_percent);

  // This page is the top of the EXTENDED funnel — tag the session before any
  // other events so the whole journey groups under one flow in analytics.
  useEffect(() => {
    setCurrentSignupFlow('extended');
    trackSignupEventOnce('partner:page_view:landing', {
      eventType: 'page_view',
      step: 'landing',
      flow: 'extended',
      data: { partner_flow: true },
      referralCode: offer.code ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTrial = (cta: string) => {
    trackSignupEvent({
      eventType: 'click',
      step: 'landing',
      flow: 'extended',
      data: { cta },
      referralCode: offer.code ?? null,
    });
    navigate('/sign-up-new-business');
  };

  const scrollToFeatures = () => {
    document.getElementById('partner-features')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#050507' }}>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden px-6 pt-10 pb-16 text-center">
        {/* Teal spotlight glow */}
        <div
          className="pointer-events-none absolute left-1/2 top-[110px] -translate-x-1/2 h-[420px] w-[720px] rounded-full"
          style={{ background: 'radial-gradient(ellipse at center, rgba(45,212,191,0.22) 0%, rgba(45,212,191,0.08) 40%, transparent 70%)' }}
        />

        {/* Stat badges */}
        <div className="relative flex items-center justify-center gap-10 flex-wrap">
          <StatBadge
            top={
              <>
                5
                <span className="flex gap-[1px]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 fill-emerald-400 text-emerald-400" />
                  ))}
                </span>
              </>
            }
            bottom="rated by owners"
          />
          <StatBadge top={<>5000+</>} bottom="businesses run on Nexus" />
        </div>

        {/* Wordmark — official Nexus logo */}
        <div className="relative mt-12 flex justify-center">
          <img
            src="/Nexus%20Full%20Logo%20(512%20x%20150%20px).png"
            alt="Nexus"
            className="h-12 sm:h-14 w-auto select-none"
            draggable={false}
          />
        </div>

        {/* Invited-by badge */}
        <div className="relative mt-8 flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-[13px] font-semibold text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" />
            You&apos;ve been invited by {offer.partner_name || 'a Nexus partner'}
          </div>
        </div>

        {/* Headline */}
        <h1 className="relative mt-6 text-4xl sm:text-5xl lg:text-[56px] font-extrabold leading-[1.08] tracking-tight max-w-3xl mx-auto">
          Scale your service business faster and smarter
        </h1>
        <p className="relative mt-5 text-[15px] sm:text-base text-white/55 max-w-xl mx-auto">
          CRM, scheduling, field management, marketing, and AI — all connected in one platform.
        </p>

        {/* Offer line */}
        <p className="relative mt-4 text-[14px] text-emerald-300/90 font-medium">
          10-day free trial
          {hasDiscount ? ` · ${formatReferralDiscountLabel(offer.signup_discount_percent!)}` : ''}
        </p>

        {/* CTA */}
        <div className="relative mt-7">
          <button
            type="button"
            onClick={() => startTrial('hero_start_free_trial')}
            className="inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-600 px-8 py-3.5 text-[16px] font-semibold text-white transition-colors shadow-[0_8px_30px_rgba(59,130,246,0.35)]"
          >
            Start Free Trial <ArrowRight className="h-5 w-5 text-emerald-300" />
          </button>
        </div>

        <button
          type="button"
          onClick={scrollToFeatures}
          className="relative mt-8 inline-flex flex-col items-center text-[13px] text-white/50 hover:text-white/80 transition-colors"
        >
          Explore Features
          <ChevronDown className="h-4 w-4 mt-0.5 animate-bounce" />
        </button>
      </section>

      {/* ── Features ── */}
      <section id="partner-features" className="px-6 py-16 max-w-5xl mx-auto">
        <h3 className="text-center text-2xl sm:text-3xl font-bold">
          Everything your business runs on — <span className="text-emerald-400">in one place</span>
        </h3>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-400/10 border border-emerald-400/20">
                  <Icon className="h-5 w-5 text-emerald-400" />
                </div>
                <div className="mt-3 text-[15px] font-semibold">{f.title}</div>
                <p className="mt-1 text-[13px] text-white/50 leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-white/60 text-[14px] mb-4">
            Join 5000+ service businesses already running on Nexus
            {hasDiscount ? ` — with ${formatReferralDiscountLabel(offer.signup_discount_percent!).toLowerCase()} from ${offer.partner_name || 'your partner'}` : ''}.
          </p>
          <button
            type="button"
            onClick={() => startTrial('footer_start_free_trial')}
            className="inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-600 px-8 py-3.5 text-[16px] font-semibold text-white transition-colors"
          >
            Start Free Trial <ArrowRight className="h-5 w-5 text-emerald-300" />
          </button>
        </div>
      </section>
    </div>
  );
}
